import React from 'react';

import { styled } from '../theme';
import InputComment from './InputComment';
import SvgIcon from './SvgIcon';
import Text from './Text';

const StyledIcon = styled(SvgIcon)<Props>({
    display: 'inline-block',
    margin: '0 8px 0 0',
});

type Props = {
    name?: string;
    checked?: boolean;
    label?: string | React.ReactNode;
    iconProps?: Record<any, object>;
    disabled?: boolean;
    onChange?: (ev: any) => void;
    readOnly?: boolean;
    onCommentChange?: (ev: { target: { value: any } }) => void;
    comments?: Record<number, string>;
    commentable?: boolean;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    pasteWarnings?: Record<number, boolean>;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

const LabelWrapper = styled('span')({
    width: '100%',
});

const Container = styled('label')<Props>(({ theme, disabled }) => ({
    margin: 0,
    cursor: 'pointer',
    padding: '0 8px 0 0',
    display: 'flex',
    alignItems: 'center',
    '> *': {
        verticalAlign: 'middle',
    },
    ...(disabled && {
        color: theme.color.disabled,
        cursor: 'default',
        pointerEvents: 'none',
    }),
}));

const Label = styled(Text)<Props>(({ theme, disabled }) => ({
    fontSize: '1.6rem',
    ...(disabled && { color: '#B9BABA' }),
}));

const StyledInput = styled('input')<Props>(({ theme }) => ({
    display: 'none',
}));

const Checkbox: React.FC<Props> = ({
    checked = false,
    iconProps = {
        true: {
            src: '/icons/ic-checkbox-selected.svg',
            size: 'xl',
        },
        false: {
            src: '/icons/ic-checkbox.svg',
            size: 'xl',
        },
    },
    disabled = false,
    children,
    onChange,
    name,
    readOnly,
    label,
    commentable,
    onCommentChange,
    comments,
    onPaste,
    pasteWarnings,
    onBlur,
    ...props
}) => (
    <Container disabled={disabled} {...props}>
        <StyledInput
            type="checkbox"
            name={name}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            readOnly={readOnly}
        />
        <StyledIcon {...iconProps[checked && !disabled]} />
        <LabelWrapper>
            <Label disabled={disabled}>{label || children}</Label>
            {commentable && (
                <InputComment
                    name={name}
                    value={comments?.[name] || ''}
                    onChange={onCommentChange}
                    onPaste={onPaste}
                    showWarning={pasteWarnings?.[name]}
                    onBlur={onBlur}
                />
            )}
        </LabelWrapper>
    </Container>
);

export default Checkbox;
